import { useMemo } from 'react';

interface Props {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  fillColor?: string;
  strokeWidth?: number;
  className?: string;
}

/**
 * Mini wykres liniowy (sparkline) — do KPI kart.
 * Renderuje SVG bez osi, etykiet — czysta linia trendu.
 */
export function Sparkline({
  data,
  width = 120,
  height = 36,
  color = '#2E5BFF',
  fillColor,
  strokeWidth = 1.5,
  className,
}: Props) {
  const path = useMemo(() => {
    if (data.length < 2) return '';
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const padding = 2;

    const points = data.map((v, i) => ({
      x: padding + (i / (data.length - 1)) * (width - padding * 2),
      y: padding + (1 - (v - min) / range) * (height - padding * 2),
    }));

    const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return line;
  }, [data, width, height]);

  const fillPath = useMemo(() => {
    if (!fillColor || data.length < 2) return '';
    const padding = 2;
    return `${path} L ${padding + width - padding * 2} ${height - padding} L ${padding} ${height - padding} Z`;
  }, [path, fillColor, data, width, height]);

  if (data.length < 2) return null;

  return (
    <svg width={width} height={height} className={className} viewBox={`0 0 ${width} ${height}`}>
      {fillColor && <path d={fillPath} fill={fillColor} opacity={0.15} />}
      <path d={path} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
